import React, { useState } from 'react';

import AccessRow from './AccessRow';
import { accessSummary, grantedModules, scopedMenuNote } from './homeAccess';

/**
 * "Your access": one line saying what this user can reach, and the full
 * breakdown behind a link.
 *
 * THE LINE IS THE DEFAULT AND THE ROWS ARE THE EXCEPTION. Five labelled chip
 * rows at the foot of every Home page is a lot of surface for a question most
 * users ask once, when they first wonder why a screen is narrower than a
 * colleague's. The summary answers it in a sentence; the rows are there for the
 * user who needs to check exactly which departments.
 *
 * Every row answers when it is open, including the empty ones — a row reading
 * "None" tells the user they hold nothing on that axis, which is information a
 * scoped user needs in order to know their slice is deliberate rather than
 * broken. That is only true WITH the label beside it, which is why the summary
 * line above leaves an empty axis out entirely rather than saying "0 venues".
 *
 * That reasoning holds only where "None" is one possible answer among several.
 * A user who holds NOTHING on any axis would get a panel of nothing but None,
 * which says nothing at all — HomeEmptyState renders the summary for them
 * instead, and this panel is not shown.
 *
 * A whole-of-event user collapses to "All departments" / "All venues" rather
 * than enumerating. Enumerating is not merely long for them, it is misleading:
 * the list would go stale the moment a department is added, while "all" stays
 * true.
 *
 * A department-scoped user gets a note under Modules explaining why a
 * Procurement, R.A.D.I.O. or Project module they hold is missing from the
 * sidebar.
 */
const ALL = 'all';

/* The revenue row merges two DIFFERENT TABLES with independent AUTO_INCREMENT
   sequences, so a Revdepartment and a GeneralRevdepartment can both be id 3.
   Keys are namespaced per axis to keep them unique in the merged list — small
   integer ids colliding across the two is ordinary, not exotic, and this tree
   re-renders once a minute behind the clock, so a duplicate key can settle a
   chip on the wrong label. HomeCountsResolver documents the same hazard from
   the query side. */
const namespaced = (rows, axis) => (rows || []).map((row) => ({ ...row, id: `${axis}-${row.id}` }));

export default function YourAccessPanel({ access = {} }) {
    const [open, setOpen] = useState(false);

    const fullAccess = !!access.full_access;
    const records = (rows) => rows || [];
    const everything = (label) => [{ id: ALL, name: label }];

    const departments = fullAccess ? everything($t('All departments')) : records(access.departments);
    const venues = fullAccess ? everything($t('All venues')) : records(access.venues);
    const revenueDepartments = fullAccess
        ? everything($t('All revenue departments'))
        : [...namespaced(access.revenue_departments, 'rev'), ...namespaced(access.general_revenue_departments, 'general')];
    // Costcenter and Revcostcenter are two different tables with independent
    // AUTO_INCREMENT sequences, the same hazard the revenue row guards against
    // above.
    const costCentres = fullAccess
        ? everything($t('All cost centres'))
        : [...namespaced(access.cost_centres, 'cc'), ...namespaced(access.revenue_cost_centres, 'revcc')];
    const modulesNote = scopedMenuNote(access);

    return (
        <div className="tw-rounded-lg tw-bg-white tw-px-6 tw-py-5 tw-shadow-primary">
            <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
                <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-charcoal">{$t('Your access')}</h3>
                <p className="tw-mb-0 tw-text-0.5sm tw-text-charcoal-60">{accessSummary(access)}</p>
                <button
                    type="button"
                    onClick={() => setOpen((shown) => !shown)}
                    aria-expanded={open}
                    aria-controls="home-access-details"
                    className="tw-ms-auto tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-blue"
                >
                    {open ? $t('Hide details') : $t('Show details')}
                </button>
            </div>

            {/* Rendered only while open. There is nothing here a collapsed
                region needs to keep — no scroll position, no fetch, no form —
                so hiding it with CSS would only leave five rows of chips in the
                accessibility tree for a screen reader to walk past. The
                aria-controls above still points at an element that exists
                whenever the state it describes is "expanded". */}
            {open && (
                <div id="home-access-details" className="tw-mt-3">
                    <AccessRow label={$t('Departments')} items={departments} emptyLabel={$t('None')} />
                    <AccessRow label={$t('Cost centres')} items={costCentres} emptyLabel={$t('None')} />
                    <AccessRow label={$t('Revenue departments')} items={revenueDepartments} emptyLabel={$t('None')} />
                    <AccessRow label={$t('Venues')} items={venues} emptyLabel={$t('None')} />
                    <AccessRow label={$t('Modules')} items={grantedModules(access.modules)} emptyLabel={$t('None')} note={modulesNote} />
                </div>
            )}
        </div>
    );
}
